import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { DAYS, IDaySchedule, TIME_OPTIONS, dayErrors } from '../../../core/utils/schedule.util';

/**
 * Horario por día con varios turnos (Agregar negocio · paso 3 y Ajustes · Horario).
 * Switch abierto/cerrado, «Agregar horario», papelera por turno y «Copiar lunes a todos».
 */
@Component({
  selector: 'app-schedule-editor',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="head">
      <p class="cr-eyebrow cr-text-tertiary">Horario</p>
      <button type="button" class="cr-btn cr-btn--link" (click)="copyMonday()">Copiar lunes a todos</button>
    </div>
    @for (d of days(); track d.day; let i = $index) {
      <div class="day" [class.is-closed]="!d.open">
        <div class="day__head">
          <span class="day__name">{{ labels[d.day - 1].label }}</span>
          @if (!d.open) { <span class="closed">Cerrado</span> }
          <app-switch [checked]="d.open" [disabled]="disabled()" [label]="'Abrir ' + labels[d.day - 1].label" (toggled)="setOpen(i, $event)" />
        </div>
        @if (d.open) {
          @for (r of d.ranges; track $index; let j = $index) {
            <div class="range">
              <div class="range__field">
                <ng-select class="cr-ng-select--bare" [items]="times" [ngModel]="r.opensAt" [ngModelOptions]="{ standalone: true }" [disabled]="disabled()"
                  (ngModelChange)="setTime(i, j, 'opensAt', $event)" [clearable]="false" [searchable]="false" [ariaLabel]="'Abre ' + labels[d.day - 1].label" />
                <span>–</span>
                <ng-select class="cr-ng-select--bare" [items]="times" [ngModel]="r.closesAt" [ngModelOptions]="{ standalone: true }" [disabled]="disabled()"
                  (ngModelChange)="setTime(i, j, 'closesAt', $event)" [clearable]="false" [searchable]="false" [ariaLabel]="'Cierra ' + labels[d.day - 1].label" />
                <cr-icon class="clock" name="clock" [size]="18" />
              </div>
              <button type="button" class="trash" aria-label="Quitar horario" (click)="removeRange(i, j)"><cr-icon name="trash" [size]="18" /></button>
            </div>
          }
          @if (errors()[i]; as err) { <p class="cr-field__error">{{ err }}</p> }
          <button type="button" class="add" (click)="addRange(i)"><cr-icon name="plus" [size]="18" />Agregar horario</button>
        }
      </div>
    }
  `,
  styles: [`
    :host{display:flex;flex-direction:column}
    .head{display:flex;align-items:center;justify-content:space-between;margin-bottom:8px}
    .head p{margin:0}
    .day{display:flex;flex-direction:column;gap:6px;padding:10px 0;border-bottom:1px solid var(--cr-border-subtle)}
    .day:last-child{border-bottom:0}
    .day__head{display:flex;align-items:center;gap:12px;min-height:36px}
    .day__name{flex:1;font:600 15px/22px var(--cr-font-body)}
    .is-closed .day__name{color:var(--cr-text-tertiary);font-weight:400}
    .closed{font:400 15px/22px var(--cr-font-body);color:var(--cr-text-tertiary)}
    .range{display:flex;gap:8px;align-items:center}
    .range__field{position:relative;flex:1;min-width:0;height:44px;display:flex;align-items:center;gap:4px;padding:0 40px 0 10px;border:1.2px solid var(--cr-border-subtle);border-radius:12px;background:#fff}
    .range__field:focus-within{border:2px solid var(--cr-brand-teal)}
    .range__field ng-select{flex:1;min-width:0}
    .range__field span{color:var(--cr-text-secondary)}
    .clock{position:absolute;right:14px;color:var(--cr-text-secondary);pointer-events:none}
    .trash{width:44px;height:44px;border-radius:12px;border:0;background:var(--cr-bg-surface-2);display:grid;place-items:center;cursor:pointer;color:var(--cr-text-primary);flex:none}
    .add{align-self:flex-start;display:inline-flex;align-items:center;gap:8px;border:0;background:none;padding:4px 0;color:var(--cr-brand-teal);font:600 14px/18px var(--cr-font-body);cursor:pointer}
    .add cr-icon{color:var(--cr-text-primary)}
  `],
})
export class ScheduleEditorComponent {
  readonly days = model.required<IDaySchedule[]>();
  /** Solo lectura (p. ej. negocio inactivo): sin cambiar días, horarios ni turnos. */
  readonly disabled = input<boolean>(false);
  readonly labels = DAYS;
  readonly times = TIME_OPTIONS;
  readonly errors = computed(() => this.days().map(d => dayErrors(d)));

  private update(fn: (days: IDaySchedule[]) => void): void {
    if (this.disabled()) return;
    const copy = this.days().map(d => ({ ...d, ranges: d.ranges.map(r => ({ ...r })) }));
    fn(copy);
    this.days.set(copy);
  }

  setOpen(i: number, open: boolean): void {
    this.update(d => {
      d[i].open = open;
      if (open && !d[i].ranges.length) d[i].ranges = [{ opensAt: '09:00', closesAt: '18:00' }];
    });
  }

  setTime(i: number, j: number, key: 'opensAt' | 'closesAt', value: string): void {
    this.update(d => { d[i].ranges[j][key] = value; });
  }

  addRange(i: number): void {
    this.update(d => {
      const last = d[i].ranges[d[i].ranges.length - 1];
      const start = last ? last.closesAt : '09:00';
      const [h] = start.split(':').map(Number);
      const end = `${String(Math.min(23, h + 4)).padStart(2, '0')}:00`;
      d[i].ranges.push({ opensAt: start, closesAt: end > start ? end : '23:59' });
    });
  }

  removeRange(i: number, j: number): void {
    this.update(d => {
      d[i].ranges.splice(j, 1);
      if (!d[i].ranges.length) { d[i].open = false; d[i].ranges = [{ opensAt: '09:00', closesAt: '18:00' }]; }
    });
  }

  copyMonday(): void {
    this.update(d => {
      const monday = d[0];
      for (let i = 1; i < d.length; i++) {
        d[i].open = monday.open;
        d[i].ranges = monday.ranges.map(r => ({ ...r }));
      }
    });
  }
}
